// components/Dashboard/tabs/SiteTrackingTab.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './SiteTrackingTab.css';

const SiteTrackingTab = ({ API_URL, showMessage }) => {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingScript, setEditingScript] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    script_code: '',
    is_active: 1
  });
  const [refreshTimestamps, setRefreshTimestamps] = useState({});
  const [openStats, setOpenStats] = useState({});
  const [statsData, setStatsData] = useState({});
  const [statsLoading, setStatsLoading] = useState({});
  const iframeRefs = useRef({});

  useEffect(() => {
    fetchScripts();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      refreshAllIframes();
    }, 30000);
    return () => clearInterval(interval);
  }, [scripts]);

  const fetchScripts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/tracking`);
      setScripts(response.data.scripts || []);
    } catch (error) {
      console.error('Scripts fetch error:', error);
      showMessage('Scriptler yüklenemedi', 'error');
    } finally {
      setLoading(false);
    }
  };

  const refreshAllIframes = () => {
    const newTimestamps = {};
    scripts.forEach(script => {
      newTimestamps[script.id] = Date.now();
    });
    setRefreshTimestamps(newTimestamps);
  };

  const refreshSingleIframe = (scriptId) => {
    setRefreshTimestamps(prev => ({
      ...prev,
      [scriptId]: Date.now()
    }));
    showMessage('Onizleme yenilendi', 'success');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.script_code) {
      showMessage('Isim ve script kodu gereklidir', 'error');
      return;
    }
    try {
      if (editingScript) {
        await axios.put(`${API_URL}/tracking/${editingScript.id}`, formData);
        showMessage('Script guncellendi!', 'success');
      } else {
        await axios.post(`${API_URL}/tracking`, formData);
        showMessage('Script eklendi!', 'success');
      }
      setIsModalOpen(false);
      setEditingScript(null);
      setFormData({ name: '', script_code: '', is_active: 1 });
      fetchScripts();
    } catch (error) {
      console.error('Script save error:', error);
      showMessage(error.response?.data?.error || 'Script kaydedilemedi', 'error');
    }
  };

  const handleEdit = (script) => {
    setEditingScript(script);
    setFormData({
      name: script.name,
      script_code: script.script_code,
      is_active: script.is_active
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu scripti silmek istediginize emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/tracking/${id}`);
      showMessage('Script silindi!', 'success');
      fetchScripts();
    } catch (error) {
      console.error('Script delete error:', error);
      showMessage('Script silinemedi', 'error');
    }
  };

  const handleToggle = async (id) => {
    try {
      await axios.patch(`${API_URL}/tracking/${id}/toggle`);
      showMessage('Script durumu guncellendi', 'success');
      fetchScripts();
    } catch (error) {
      console.error('Script toggle error:', error);
      showMessage('Durum guncellenemedi', 'error');
    }
  };

  const handleCopyScript = (scriptCode) => {
    navigator.clipboard.writeText(scriptCode);
    showMessage('Script kopyalandi!', 'success');
  };

  const handleCopyPixelCode = (scriptId, scriptName) => {
    const pixelCode = `<amp-pixel src="https://api.autogram.site/api/tracking/t.gif?id=${scriptId}&url=SOURCE_URL&ref=DOCUMENT_REFERRER&title=TITLE"></amp-pixel>`;
    navigator.clipboard.writeText(pixelCode);
    showMessage(`${scriptName} - AMP Pixel kodu kopyalandi!`, 'success');
  };

  const handleCopyTrackingScript = (scriptId, scriptName) => {
    const code = `<script src="https://api.autogram.site/api/tracking/track.js?id=${scriptId}" async></script>`;
    navigator.clipboard.writeText(code);
    showMessage(`${scriptName} - Tracking script kopyalandi!`, 'success');
  };

  const toggleStats = async (scriptId) => {
    const isOpen = openStats[scriptId];
    setOpenStats(prev => ({ ...prev, [scriptId]: !isOpen }));

    if (!isOpen && !statsData[scriptId]) {
      await fetchStats(scriptId);
    }
  };

  const fetchStats = async (scriptId, startDate, endDate) => {
    setStatsLoading(prev => ({ ...prev, [scriptId]: true }));
    try {
      let url = `${API_URL}/tracking/${scriptId}/stats`;
      const params = [];
      if (startDate) params.push(`startDate=${startDate}`);
      if (endDate) params.push(`endDate=${endDate}`);
      if (params.length > 0) url += '?' + params.join('&');

      const response = await axios.get(url);
      setStatsData(prev => ({ ...prev, [scriptId]: response.data.stats }));
    } catch (error) {
      console.error('Stats fetch error:', error);
      showMessage('Istatistikler yuklenemedi', 'error');
    } finally {
      setStatsLoading(prev => ({ ...prev, [scriptId]: false }));
    }
  };

  const getMaxPageviews = (dailyStats) => {
    if (!dailyStats || dailyStats.length === 0) return 1;
    return Math.max(...dailyStats.map(d => d.pageviews), 1);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingScript(null);
    setFormData({ name: '', script_code: '', is_active: 1 });
  };

  return (
    <div className="site-tracking-tab">
      <div className="tab-header">
        <div className="header-actions">
          <button onClick={refreshAllIframes} className="refresh-all-button" title="Tum onizlemeleri yenile">
            Tumunu Yenile
          </button>
          <button onClick={() => setIsModalOpen(true)} className="add-button">
            + Yeni Script Ekle
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Yukleniyor...</div>
      ) : scripts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>Henuz script eklenmemis</h3>
          <p>Ilk site takip scriptinizi ekleyerek baslayin</p>
          <button onClick={() => setIsModalOpen(true)} className="add-button">
            Script Ekle
          </button>
        </div>
      ) : (
        <div className="scripts-grid">
          {scripts.map(script => (
            <div key={script.id} className={`script-card ${openStats[script.id] ? 'stats-open' : ''}`}>
              <div className="script-header">
                <div className="script-title">
                  <h3>{script.name}</h3>
                  <span className={`status-badge ${script.is_active ? 'active' : 'inactive'}`}>
                    {script.is_active ? 'Aktif' : 'Pasif'}
                  </span>
                </div>
                <div className="script-actions">
                  <button onClick={() => toggleStats(script.id)} className={`action-btn stats-btn ${openStats[script.id] ? 'stats-active' : ''}`} title="Istatistikler">
                    📊
                  </button>
                  <button onClick={() => refreshSingleIframe(script.id)} className="action-btn refresh-btn" title="Onizlemeyi Yenile">
                    🔄
                  </button>
                  <button onClick={() => handleToggle(script.id)} className="action-btn toggle-btn" title={script.is_active ? 'Devre Disi Birak' : 'Etkinlestir'}>
                    {script.is_active ? '⏸️' : '▶️'}
                  </button>
                  <button onClick={() => handleEdit(script)} className="action-btn edit-btn" title="Duzenle">
                    ✏️
                  </button>
                  <button onClick={() => handleDelete(script.id)} className="action-btn delete-btn" title="Sil">
                    🗑️
                  </button>
                </div>
              </div>

              <div className="script-preview-container">
                <iframe
                  key={refreshTimestamps[script.id] || script.id}
                  ref={el => iframeRefs.current[script.id] = el}
                  srcDoc={`
                    <!DOCTYPE html>
                    <html>
                      <head>
                        <meta charset="utf-8">
                        <meta http-equiv="cache-control" content="no-cache, no-store, must-revalidate">
                        <meta http-equiv="pragma" content="no-cache">
                        <meta http-equiv="expires" content="0">
                        <style>
                          body { margin: 0; padding: 10px; font-family: Arial, sans-serif; background: transparent; overflow: hidden; }
                          * { max-width: 100%; }
                        </style>
                      </head>
                      <body>
                        ${script.script_code}
                        <script>console.log('Loaded at:', new Date().toISOString());</script>
                      </body>
                    </html>
                  `}
                  title={`Preview ${script.name}`}
                  sandbox="allow-scripts allow-same-origin"
                  className="script-preview-frame"
                />
                <button onClick={() => handleCopyScript(script.script_code)} className="copy-script-btn" title="Script kodunu kopyala">
                  📋
                </button>
                <button onClick={() => handleCopyPixelCode(script.id, script.name)} className="copy-pixel-btn" title="AMP Pixel kodunu kopyala">
                  📡
                </button>
                <button onClick={() => handleCopyTrackingScript(script.id, script.name)} className="copy-tracking-btn" title="Tracking script kodunu kopyala">
                  {'</>'}
                </button>
              </div>

              {/* İstatistikler Paneli */}
              {openStats[script.id] && (
                <div className="stats-panel">
                  {statsLoading[script.id] ? (
                    <div className="stats-loading">Istatistikler yukleniyor...</div>
                  ) : statsData[script.id] ? (
                    <>
                      {/* Ozet Kartlari */}
                      <div className="stats-summary">
                        <div className="stats-summary-card">
                          <div className="stats-summary-value">{(statsData[script.id].totals?.total_pageviews || 0).toLocaleString('tr-TR')}</div>
                          <div className="stats-summary-label">Sayfa Goruntulenme</div>
                        </div>
                        <div className="stats-summary-card">
                          <div className="stats-summary-value">{(statsData[script.id].totals?.unique_visitors || 0).toLocaleString('tr-TR')}</div>
                          <div className="stats-summary-label">Tekil Ziyaretci</div>
                        </div>
                        <div className="stats-summary-card">
                          <div className="stats-summary-value">{statsData[script.id].totals?.active_days || 0}</div>
                          <div className="stats-summary-label">Aktif Gun</div>
                        </div>
                      </div>
                      {/* Kaynak Bazli Istatistikler */}
                      {statsData[script.id].sourceStats && statsData[script.id].sourceStats.length > 0 && (
                        <div className="stats-section">
                          <h4>Kaynak Bazlı Dağılım</h4>
                          <div className="stats-source-cards">
                            {statsData[script.id].sourceStats.map((src, index) => (
                              <div key={index} className={`stats-source-card ${src.source === 'Script' ? 'source-script' : 'source-pixel'}`}>
                                <div className="stats-source-icon">{src.source === 'Script' ? '⚡' : '📡'}</div>
                                <div className="stats-source-info">
                                  <div className="stats-source-name">{src.source}</div>
                                  <div className="stats-source-value">{src.total.toLocaleString('tr-TR')} görüntülenme</div>
                                  <div className="stats-source-unique">{src.unique_visitors.toLocaleString('tr-TR')} tekil</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Gunluk Grafik */}
                      {statsData[script.id].dailyStats && statsData[script.id].dailyStats.length > 0 && (
                        <div className="stats-section">
                          <h4>Son 30 Gun</h4>
                          <div className="stats-chart">
                            {[...statsData[script.id].dailyStats].reverse().map((day, index) => (
                              <div key={index} className="chart-bar-wrapper" title={`${new Date(day.date).toLocaleDateString('tr-TR')}: ${day.pageviews} goruntulenme, ${day.unique_visitors} tekil`}>
                                <div
                                  className="chart-bar"
                                  style={{ height: `${Math.max((day.pageviews / getMaxPageviews(statsData[script.id].dailyStats)) * 100, 4)}%` }}
                                >
                                  <span className="chart-bar-tooltip">{day.pageviews}</span>
                                </div>
                                <div className="chart-bar-label">
                                  {new Date(day.date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* En Cok Ziyaret Edilen Sayfalar */}
                      {statsData[script.id].topPages && statsData[script.id].topPages.length > 0 && (
                        <div className="stats-section">
                          <h4>En Cok Ziyaret Edilen Sayfalar</h4>
                          <div className="stats-table">
                            <div className="stats-table-header">
                              <span>Sayfa</span>
                              <span>Goruntulenme</span>
                              <span>Tekil</span>
                            </div>
                            {statsData[script.id].topPages.map((page, index) => (
                              <div key={index} className="stats-table-row">
                                <span className="stats-page-url" title={page.page_url}>
                                  {page.page_url.length > 50 ? page.page_url.substring(0, 50) + '...' : page.page_url}
                                </span>
                                <span className="stats-page-count">{page.pageviews}</span>
                                <span className="stats-page-count">{page.unique_visitors}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Referrer'lar */}
                      {statsData[script.id].topReferrers && statsData[script.id].topReferrers.length > 0 && (
                        <div className="stats-section">
                          <h4>Trafik Kaynaklari</h4>
                          <div className="stats-table">
                            <div className="stats-table-header">
                              <span>Kaynak</span>
                              <span>Ziyaret</span>
                            </div>
                            {statsData[script.id].topReferrers.map((ref, index) => (
                              <div key={index} className="stats-table-row">
                                <span className="stats-page-url" title={ref.referrer}>
                                  {ref.referrer.length > 50 ? ref.referrer.substring(0, 50) + '...' : ref.referrer}
                                </span>
                                <span className="stats-page-count">{ref.visits}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Yenile Butonu */}
                      <div className="stats-refresh">
                        <button onClick={() => fetchStats(script.id)} className="stats-refresh-btn">
                          Istatistikleri Yenile
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="stats-empty">Henuz istatistik verisi yok</div>
                  )}
                </div>
              )}

              <div className="script-footer">
                <small>
                  Olusturulma: {new Date(script.created_at).toLocaleDateString('tr-TR')}
                </small>
                {script.updated_at !== script.created_at && (
                  <small>
                    Guncelleme: {new Date(script.updated_at).toLocaleDateString('tr-TR')}
                  </small>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingScript ? 'Script Duzenle' : 'Yeni Script Ekle'}</h3>
              <button onClick={closeModal} className="close-btn">✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Script Ismi *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Orn: Waust Visitor Counter"
                  required
                />
              </div>
              <div className="form-group">
                <label>Script Kodu *</label>
                <textarea
                  value={formData.script_code}
                  onChange={e => setFormData({ ...formData, script_code: e.target.value })}
                  placeholder='<script id="_wau989">var _wau = _wau || [];</script>'
                  rows={6}
                  required
                />
                <small className="help-text">
                  Ziyaretci sayaci veya analytics script kodunu buraya yapistirin
                </small>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active === 1}
                    onChange={e => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                  />
                  <span>Scripti aktif et</span>
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={closeModal} className="cancel-btn">Iptal</button>
                <button type="submit" className="submit-btn">{editingScript ? 'Guncelle' : 'Ekle'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteTrackingTab;